import { TaskTypeCornerPill } from "components/TaskTypePill/TaskTypePill";
import type { MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useNavigate } from "react-router-dom";
import { getTaskDestinationUrl } from "pages/tasks/utils/task-url-utils";
import { setCurrentTask } from "reducers/files";
import { setShowTaskModal } from "reducers/main";
import type { TaskDataType } from "types/DataTypes";
import {
  getDateFromISOStringDatePart,
  getTimeFromISOString,
} from "utils/date-utils.ts";
import { useDispatch, useSelector } from "utils/redux";

import CMSResponseTaskItem from "./CMSResponseTaskItem";
import {
  formatClaimNumbers,
  getGenericTaskMessage,
  getRushType,
  getValidationCounts,
  isOpsTask,
  shouldOpenTaskModal,
} from "../../utils/kaboodleSwimlaneItemUtils";
import type { TaskStatus } from "../../utils/taskUtils";

type KaboodleSwimlaneItemType = {
  task: TaskDataType;
  columnId: TaskStatus;
  index: number;
  moveItem: (
    itemId: number,
    sourceColumn: TaskStatus,
    targetColumn: TaskStatus,
    targetIndex: number,
  ) => void;
  isRefetching?: boolean;
};

type TaskCardHeaderProps = {
  title: string;
  titleTooltip: string;
  taskType: string;
  onEdit: (event: MouseEvent<HTMLButtonElement>) => void;
};

const TaskCardHeader = ({
  title,
  titleTooltip,
  taskType,
  onEdit,
}: TaskCardHeaderProps) => (
  <div className="task-card__header">
    <div className="task-card__title" title={titleTooltip}>
      {title}
    </div>
    <div className="task-card__header-actions">
      <button
        type="button"
        className="task-card__edit-button material-icons"
        aria-label="Edit task"
        title="Edit task"
        onClick={onEdit}
      >
        edit
      </button>
      <TaskTypeCornerPill
        taskType={taskType}
        className="task-card__type-pill"
      />
    </div>
  </div>
);

type StandardTaskContentProps = {
  task: TaskDataType;
  opsTask: boolean;
  assignedByLabel: string;
};

const StandardTaskContent = ({
  task,
  opsTask,
  assignedByLabel,
}: StandardTaskContentProps) => {
  const claimantBirthDates = (task.claimant_birth_dates ?? [])
    .filter(Boolean)
    .map((dob) => getTimeFromISOString(dob, "date"));
  const claimantSsns = (task.claimant_ssns ?? []).filter(Boolean);
  const taskMessage = getGenericTaskMessage(task.message);

  return (
    <div className="task-card__content">
      {opsTask ? (
        <>
          <div className="task-card__primary-info">
            <div className="task-card__message">
              {task.message || "No message provided"}
            </div>
          </div>
          <div className="task-card__secondary-info">
            <div className="task-card__assigned-by">
              Assigned by: {assignedByLabel}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="task-card__primary-info">
            <div className="task-card__client">
              <span className="task-card__label">Client:</span>{" "}
              {task.client_name}
            </div>
            <div className="task-card__claimant">
              <span className="task-card__label">Claimant:</span>{" "}
              {task.claimant_names}
            </div>
          </div>
          <div className="task-card__secondary-info">
            <div className="task-card__injury-date">
              <span className="task-card__label">DOI:</span>{" "}
              {task.dates_of_injury?.length
                ? task.dates_of_injury.join(", ")
                : "Not specified"}
            </div>
            {claimantBirthDates.length > 0 && (
              <div className="task-card__birth-date">
                <span className="task-card__label">D.O.B:</span>{" "}
                {claimantBirthDates.join(", ")}
              </div>
            )}
            {claimantSsns.length > 0 && (
              <div className="task-card__ssn">
                <span className="task-card__label">SSN:</span>{" "}
                {claimantSsns.join(", ")}
              </div>
            )}
            {taskMessage && (
              <div className="task-card__message">{taskMessage}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

type TaskCardMetadataProps = {
  task: TaskDataType;
  opsTask: boolean;
  hasAssignedService: boolean;
  errorCount: number;
  warningCount: number;
};

const TaskCardMetadata = ({
  task,
  opsTask,
  hasAssignedService,
  errorCount,
  warningCount,
}: TaskCardMetadataProps) => (
  <div className="task-card__metadata">
    <div className="task-card__footer-left">
      {task.products?.length > 0 &&
        (hasAssignedService ? (
          <div className="task-card__services">
            {task.products.map((p, productIndex) => (
              <span key={productIndex} className="product-tag">
                {p.type}
              </span>
            ))}
          </div>
        ) : (
          <div className="task-card__services">
            <span className="product-tag">OPS</span>
          </div>
        ))}
    </div>

    <div className="task-card__tags">
      {opsTask && (
        <div className="status-tag status-tag--info">
          <span className="status-tag__text">OPS</span>
        </div>
      )}

      {task.due_date && (
        <div className="status-tag status-tag--due">
          <span className="status-tag__icon material-icons">event</span>
          <span className="status-tag__text">
            Due {getDateFromISOStringDatePart(task.due_date)}
          </span>
        </div>
      )}

      {errorCount > 0 && (
        <div className="status-tag status-tag--error">
          <span className="status-tag__icon material-icons">warning</span>
          <span className="status-tag__text">
            {errorCount} {errorCount === 1 ? "Action" : "Actions"}
          </span>
        </div>
      )}

      {errorCount === 0 && warningCount > 0 && (
        <div className="status-tag status-tag--warning">
          <span className="status-tag__icon material-icons">flash_on</span>
          <span className="status-tag__text">
            {warningCount} {warningCount === 1 ? "Warning" : "Warnings"}
          </span>
        </div>
      )}
    </div>
  </div>
);

const KaboodleSwimlaneItem = ({
  task,
  columnId,
  index,
  moveItem,
  isRefetching = false,
}: KaboodleSwimlaneItemType) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const constants = useSelector((state) => state.shared.constants);
  const openEditModal = useCallback(() => {
    dispatch(
      setCurrentTask({
        ...task,
      }),
    );
    dispatch(setShowTaskModal(true));
  }, [dispatch, task]);

  const handleOpenEditModal = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      openEditModal();
    },
    [openEditModal],
  );

  const handleCardClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const destinationUrl = getTaskDestinationUrl({
        actionUrl: task.action_url,
        fileId: task.file,
      });

      if (destinationUrl) {
        if (event.ctrlKey || event.metaKey) {
          window.open(destinationUrl);
        } else {
          navigate(destinationUrl);
        }
        return;
      }

      if (shouldOpenTaskModal(task)) {
        openEditModal();
      }
    },
    [navigate, openEditModal, task],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "ITEM",
      item: { id: task.id, columnId, index },
      canDrag: () => !isRefetching, // disable dragging while refetching
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [task.id, columnId, index, isRefetching],
  );

  const [, drop] = useDrop(() => ({
    accept: "ITEM",
    hover: (draggedItem: { id: number; columnId: string; index: number }) => {
      if (
        draggedItem.columnId === columnId &&
        draggedItem.index !== index &&
        draggedItem.id !== task.id
      ) {
        moveItem(draggedItem.id, columnId, columnId, index);
        draggedItem.index = index;
      }
    },
  }));
  const { errorCount, warningCount } = useMemo(
    () => getValidationCounts(task.validations),
    [task.validations],
  );
  const opsTask = isOpsTask(task);
  const isCmsResponseTask = task.type === "CR";
  const rushType = getRushType(task);
  const hasAssignedService = task.service?.type != null;
  const assignedByUser =
    constants?.USERS?.find((user) => user?.value === task.assigned_by) ?? null;
  const assignedByLabel = assignedByUser?.label || "Unknown";

  const title = isCmsResponseTask
    ? "CMS Response"
    : opsTask
      ? "Ops Task"
      : formatClaimNumbers(task.claim_numbers);
  const titleTooltip = isCmsResponseTask
    ? `CMS Response - Claim #${task.claim_numbers}`
    : opsTask
      ? "Ops Task"
      : `Claim #${task.claim_numbers}`;
  const cardClassName = `data-block swimlane-item task-card${rushType ? " task-card--rush" : ""}`;

  return (
    <a
      className={cardClassName}
      data-rush-type={rushType || undefined}
      ref={(node) => drag(drop(node))}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      onClick={handleCardClick}
    >
      <TaskCardHeader
        title={title}
        titleTooltip={titleTooltip}
        taskType={task.type}
        onEdit={handleOpenEditModal}
      />
      {isCmsResponseTask ? (
        <CMSResponseTaskItem task={task} />
      ) : (
        <>
          <StandardTaskContent
            task={task}
            opsTask={opsTask}
            assignedByLabel={assignedByLabel}
          />
          <TaskCardMetadata
            task={task}
            opsTask={opsTask}
            hasAssignedService={hasAssignedService}
            errorCount={errorCount}
            warningCount={warningCount}
          />
        </>
      )}
    </a>
  );
};

export default KaboodleSwimlaneItem;
